import { useState } from 'react';
import { X } from '../../icons';
import { Button } from '../../components/shared';
import { createFolder } from '../../api/workspace';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { tWorkspace } from '../../lib/workspace-i18n';

interface FolderCreateModalProps {
  parentId: string | null;
  parentName: string;
  onClose: () => void;
  onCreated: () => void;
  isLeaving?: boolean;
}

/** FolderCreateModal (P3-FILE-FE) — 단순 — name 입력 + parent 컨텍스트 표시.
 * isLeaving: useModalLeave hook 의 unmount transition 단계. */
export function FolderCreateModal({ parentId, parentName, onClose, onCreated, isLeaving = false }: FolderCreateModalProps) {
  const trapRef = useFocusTrap<HTMLDivElement>(true);
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEscapeClose(onClose, submitting);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setError(tWorkspace('workspace.modal.err.empty_name'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await createFolder({ name: name.trim(), parentId: parentId ?? null });
      onCreated();
    } catch (err) {
      const code = (err as any)?.response?.data?.code ?? '';
      const msg = (err as any)?.response?.data?.message ?? (err as Error).message;
      setError(`${code ? `[${code}] ` : ''}${msg ?? tWorkspace('workspace.modal.err.save_failed')}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className={`workspace-modal-backdrop${isLeaving ? ' workspace-modal-backdrop--leaving' : ''}`}
      onClick={(e) => { if (e.target === e.currentTarget && !submitting && !isLeaving) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={tWorkspace('workspace.modal.aria_dialog')}
      aria-hidden={isLeaving || undefined}
    >
      <div ref={trapRef} className={`workspace-modal${isLeaving ? ' workspace-modal--leaving' : ''}`}>
        <header className="workspace-modal-header">
          <h2>{tWorkspace('workspace.modal.heading')}</h2>
          <Button variant="ghost" size="sm" iconOnly
            onClick={onClose} disabled={submitting} aria-label={tWorkspace('workspace.modal.aria_close')}
            iconLeft={<X size={18} />}
          />
        </header>
        <form className="workspace-modal-body" onSubmit={handleSubmit}>
          <div className="workspace-modal-context">
            상위: <strong>{parentName}</strong>
          </div>
          <div className="workspace-modal-field">
            <label htmlFor="folder-name">{tWorkspace('workspace.modal.label_name')}</label>
            <input
              id="folder-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              required
              autoFocus
            />
          </div>
          {error && <div className="workspace-modal-error" role="alert">{error}</div>}
          <footer className="workspace-modal-footer">
            <Button variant="secondary"
              onClick={onClose} disabled={submitting}
            >
              {tWorkspace('workspace.modal.btn_cancel')}
            </Button>
            <Button variant="primary" type="submit"
              disabled={submitting}
            >
              {submitting ? tWorkspace('workspace.modal.btn_submit_loading') : tWorkspace('workspace.modal.btn_submit_idle')}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}
